import React, { useEffect } from 'react';
import { TFunction } from 'react-i18next';
import { useLocation } from '@tanstack/react-router';
import {
  List,
  Information,
  Spinner,
  HelpLink,
  View,
  Stack,
  Button,
  Text,
  withTranslation,
} from '@piwikpro/ui-components';
import { errorBoundary, withInject } from '@piwikpro/platform';
import { OAuthService } from '@piwikpro/oauth-crate';
import { RouterService } from '@piwikpro/router-crate';
import GenerateCredentials from './GenerateCredentials';
import { ICredentialsEntry, ICredentialsListState } from '../../redux/credentials/list';

interface InjectedProps {
  Title: typeof React.Component
  oauth: OAuthService
  routerService: RouterService
}

export interface ICredentialsProps extends InjectedProps {
  isLoading: ICredentialsListState['isLoading']
  credentials: ICredentialsListState['items']
  t: TFunction
}

const Credentials: React.FC<ICredentialsProps> = ({
  Title,
  oauth,
  routerService,
  isLoading,
  credentials,
  t,
}) => {
  const location = useLocation();

  useEffect(() => {
    const fetchData = async () => {
      try {
        await oauth.findClients();
      } catch (err) {
        // eslint-disable-next-line no-empty
      }
    };
    fetchData();
  }, [oauth]);

  const moveToCreateView = () => {
    routerService.push({ to: '/profile/api-credentials/m/create' });
  };

  const handleDeleteClient = async (id: string) => {
    try {
      await oauth.deleteClient(id);
      await oauth.findClients();
    // eslint-disable-next-line no-empty
    } catch (err) {}
  };

  const renderListItem = (item: ICredentialsEntry) => (
    <List.Item
      key={item.id}
    >
      <Text as="p" weight={600} dataId="Label" wrapType="noWrapAndCutText">
        {item.attributes.name}
      </Text>
      <Text as="p">
        {t('components:date.long', { date: new Date(item.attributes.addedAt) }) as string}
      </Text>
      <Button
        appearance="subdued"
        icon="cross"
        iconRight=""
        ariaLabel={t('credentials.actions.deleteCredential', { name: item.attributes.name })}
        onClick={() => handleDeleteClient(item.id)}
      />
    </List.Item>
  );

  const instanceAddress = document.location.origin;
  const authTokenUrl = `${instanceAddress}/auth/token`;
  const showGenerateModal = location.pathname.includes('/m/create');

  return (
    <Title part={t('credentials.title')}>
      <View>
        {showGenerateModal && (
          <GenerateCredentials />
        )}
        <View.Header title={t('credentials.title')}>
          <Button appearance="primary" text={t('credentials.actions.generateCredential')} onClick={moveToCreateView} />
        </View.Header>
        <View.Body>
          <Information type="info" dataId={`credentials-info-${authTokenUrl}`}>
            {t('credentials.infoWithUrl', { url: authTokenUrl })}
          </Information>
          <Stack vertical horizontalAlignment="middle" verticalAlignment="center">
            <Stack.Item fill>
              {isLoading
                ? <Spinner />
                : (
                  <List
                    title=" "
                    columns={[
                      {
                        name: t('credentials.tableHeaders.name') as string,
                        size: 3,
                        align: 'left',
                      },
                      {
                        name: t('credentials.tableHeaders.dateCreated') as string,
                        size: 2,
                        align: 'left',
                      },
                      {
                        name: ' ',
                        size: 1,
                        align: 'right',
                      }]}
                  >
                    {credentials.map(item => renderListItem(item))}
                  </List>
                )}
            </Stack.Item>
            <Stack.Item>
              <HelpLink href="https://developers.piwik.pro/reference/authentication">
                API keys
              </HelpLink>
            </Stack.Item>
          </Stack>
        </View.Body>
      </View>
    </Title>
  );
};

export default withInject<InjectedProps>({
  Title: 'TitleCrate.components.Title',
  oauth: 'OAuthCrate.oauth',
  routerService: 'RouterCrate.router',
})(withTranslation('profile')(errorBoundary(Credentials)));

export {
  Credentials,
};
